import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { WORLDS } from "../data/content";
import { useHub } from "../lib/hubContent";
import { EditSectionButton } from "./HubEditor";
import { scrollToSection } from "../lib/scroll";
import LogoMark from "./LogoMark";
import Flower from "./Flower";
import Marquee from "./Marquee";
import Reveal from "./Reveal";

export default function SiteFooter() {
  const [email, setEmail] = useState("");
  const c = useHub().section("footer");
  const marqueeItems = useHub().section("marquee").items;
  const brands = useHub().section("brands").brands || [];

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast("That email looks unfinished.", { description: "Give it one more look — then you're on the list." });
      return;
    }
    toast("You're on the list.", { description: "The Dispatch arrives when there's something worth dispatching." });
    setEmail("");
  };

  return (
    <footer id="contact" data-testid="site-footer" className="relative bg-[#241B12] text-[#FBF3E4] overflow-hidden">
      <EditSectionButton sectionKey="footer" label="Edit footer" />
      <div className="border-b border-white/10 py-3.5">
        <Marquee items={marqueeItems} reverse itemClassName="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FBF3E4]/40" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-12">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-20">
            <div>
              <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-en-yellow mb-6">{c.eyebrow}</p>
              <h2 className="font-display font-black uppercase leading-[0.9] tracking-tight text-[11vw] lg:text-8xl">
                Eclectically
                <br />
                <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-en-yellow via-en-pink to-en-purple">Nelle</span>
              </h2>
              <p className="mt-6 font-mono text-xs tracking-[0.3em] uppercase text-[#FBF3E4]/60">The Hub of It All.</p>
            </div>

            <form data-testid="newsletter-form" onSubmit={subscribe} className="w-full max-w-md">
              <p className="font-display text-2xl mb-2">{c.dispatch_title}</p>
              <p className="text-sm text-[#FBF3E4]/50 mb-5">{c.dispatch_blurb}</p>
              <div className="flex rounded-full border border-white/20 bg-white/5 p-1.5 focus-within:border-en-yellow transition-colors">
                <input
                  data-testid="newsletter-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.com"
                  aria-label="Email address"
                  className="flex-1 bg-transparent px-4 text-sm outline-none placeholder:text-white/30 min-w-0"
                />
                <button
                  data-testid="newsletter-submit-button"
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-full bg-en-yellow text-[#241B12] px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-en-pink hover:text-white transition-colors"
                >
                  Join
                  <ArrowRight size={13} />
                </button>
              </div>
            </form>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="grid sm:grid-cols-3 gap-12 border-t border-white/10 pt-14 mb-16">
            <nav aria-label="Worlds">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FBF3E4]/40 mb-6">The Worlds</p>
              <ul className="space-y-3">
                {WORLDS.map((w) => (
                  <li key={w.id}>
                    <a
                      href={w.href || "#hub"}
                      data-testid={`footer-world-${w.id}`}
                      onClick={scrollToSection(w.href || "#hub")}
                      className="inline-flex items-center gap-2.5 text-sm text-[#FBF3E4]/70 hover:text-white transition-colors"
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: w.color }} />
                      {w.name}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Brands">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FBF3E4]/40 mb-6">Built From Here</p>
              <ul className="space-y-3">
                {brands.map((b) => (
                  <li key={b.id}>
                    {b.id === "prismatic" ? (
                      <a
                        href="/prismatic-essence"
                        data-testid={`footer-brand-${b.id}`}
                        className="text-sm text-[#FBF3E4]/70 hover:text-white transition-colors"
                      >
                        {b.name}
                      </a>
                    ) : (
                      <a
                        href="#brands"
                        data-testid={`footer-brand-${b.id}`}
                        onClick={scrollToSection("#brands")}
                        className="text-sm text-[#FBF3E4]/70 hover:text-white transition-colors"
                      >
                        {b.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label="Connect">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FBF3E4]/40 mb-6">Connect</p>
              <div className="flex flex-wrap gap-2.5">
                {(c.socials || []).map((s) => (
                  <a
                    key={s.id}
                    href={s.url || "#contact"}
                    target={s.url && !s.url.startsWith("mailto") ? "_blank" : undefined}
                    rel={s.url ? "noopener noreferrer" : undefined}
                    data-testid={`social-${s.id}`}
                    onClick={
                      s.url
                        ? undefined
                        : (e) => {
                            e.preventDefault();
                            toast(`${s.label} links up soon.`, { description: "The hub is being wired to the outside world." });
                          }
                    }
                    className="rounded-full border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-[#FBF3E4]/70 hover:bg-white hover:text-[#241B12] transition-colors"
                  >
                    {s.label} ↗
                  </a>
                ))}
              </div>
            </nav>
          </div>
        </Reveal>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10 pt-8">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FBF3E4]/40">{c.copyright}</p>
          <p className="inline-flex items-center gap-2 font-hand text-xl text-[#FBF3E4]/50 rotate-[-1deg]">
            <Flower className="w-6 h-6" />
            {c.hand_note}
          </p>
          <LogoMark className="w-8 h-8 text-[#FBF3E4]/60" />
        </div>
      </div>
    </footer>
  );
}
